import React, { useEffect, useMemo, useState } from "react";
import {
  CBadge,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CFormInput,
  CFormLabel,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CRow,
  CTab,
  CTabContent,
  CTabList,
  CTabPanel,
  CTabs,
  CTable,
  CTooltip,
  CWidgetStatsB,
  CButton,
  CContainer,
} from "@coreui/react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { apiClient } from "services/Api";
import SearchSelect from "components/forms/SearchSelect";
import LoadingScreen from "components/globals/LoadingScreen";
import ErrorPage from "components/globals/ErrorPage";
import { CIcon } from "@coreui/icons-react";
import {
  cilChartLine,
  cilLightbulb,
  cilSettings,
  cilWarning,
} from "@coreui/icons";

const DEFAULT_WEEKLY_HOURS = 40;

const normalizeText = (value) => (value ? String(value).toLowerCase() : "");

const getProjectWeight = (project) => Number(project?.projectMultiplier) || 1;
const formatProjectCount = (value) =>
  Number.isInteger(value) ? value : value.toFixed(1);

const getPercentile = (values, percentile) => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.ceil((percentile / 100) * sorted.length) - 1;
  return sorted[Math.max(0, index)];
};

const Resources = () => {
  const [projects, setProjects] = useState(null);
  const [error, setError] = useState(false);
  const [managementUsers, setManagementUsers] = useState([]);
  const [weeklyHoursByName, setWeeklyHoursByName] = useState({});
  const [filters, setFilters] = useState({
    search: "",
    typology: [],
    states: [],
    risk: [],
    employee: [],
    publicContracting: [],
  });
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [hoursOpen, setHoursOpen] = useState(false);
  const [hoursDraft, setHoursDraft] = useState({});

  useEffect(() => {
    apiClient
      .get("/projects/resources/")
      .then((response) => {
        const projects = response.data.projects || [];
        const users = response.data.management_users || [];

        const nameHoursMap = {};
        users.forEach((user) => {
          const value = Number(user.weekly_hours);
          nameHoursMap[user.name] = Number.isFinite(value)
            ? value
            : DEFAULT_WEEKLY_HOURS;
        });

        setProjects(projects);
        setManagementUsers(users);
        setWeeklyHoursByName(nameHoursMap);
        setHoursDraft(
          users.reduce((acc, user) => {
            acc[user.teamwork_id] = Number.isFinite(user.weekly_hours)
              ? user.weekly_hours
              : DEFAULT_WEEKLY_HOURS;
            return acc;
          }, {})
        );
      })
      .catch(() => {
        setError(true);
      });
  }, []);

  const typologyOptions = useMemo(() => {
    const set = new Set();
    projects?.forEach((project) => {
      if (project.projectTypology) set.add(project.projectTypology);
    });
    return Array.from(set).sort();
  }, [projects]);

  const stateOptions = useMemo(() => {
    const set = new Set();
    projects?.forEach((project) => {
      if (project.projectState) set.add(project.projectState);
    });
    return Array.from(set).sort();
  }, [projects]);

  const riskOptions = useMemo(() => {
    const set = new Set();
    projects?.forEach((project) => {
      if (project.riskLevel) set.add(project.riskLevel);
    });
    return Array.from(set).sort();
  }, [projects]);

  const employeeOptions = useMemo(() => {
    const set = new Set();
    projects?.forEach((project) => {
      project.userAssignees?.forEach((name) => set.add(name));
    });
    return Array.from(set).sort();
  }, [projects]);

  const publicContractingOptions = useMemo(() => {
    const set = new Set();
    projects?.forEach((project) => {
      set.add(project.publicContracting);
    });
    return Array.from(set).sort();
  }, [projects]);

  const avatarByName = useMemo(() => {
    const map = {};
    managementUsers.forEach((user) => {
      map[user.name] = user.avatar_url;
    });
    return map;
  }, [managementUsers]);

  const filteredProjects = useMemo(() => {
    if (!projects) return [];
    const search = normalizeText(filters.search);

    return projects.filter((project) => {
      if (
        filters.typology.length &&
        !filters.typology.includes(project.projectTypology)
      )
        return false;
      if (
        filters.states.length &&
        !filters.states.includes(project.projectState)
      )
        return false;
      if (
        filters.employee.length &&
        !filters.employee.some((name) => project.userAssignees?.includes(name))
      )
        return false;
      if (filters.risk.length && !filters.risk.includes(project.riskLevel))
        return false;

      if (
        filters.publicContracting.length &&
        !filters.publicContracting.includes(project.publicContracting)
      )
        return false;

      if (search) {
        const haystack = [
          project.project,
          project.projectTypology,
          project.projectState,
          project.publicContracting ? "copromocao" : "",
          project.riskLevel,
          ...(project.userAssignees || []),
        ]
          .map(normalizeText)
          .join(" ");
        if (!haystack.includes(search)) return false;
      }

      return true;
    });
  }, [projects, filters]);

  const employeeStats = useMemo(() => {
    const map = new Map();
    filteredProjects.forEach((project) => {
      const assignees = project.userAssignees || [];
      if (!assignees.length) return;

      assignees.forEach((name) => {
        if (!map.has(name)) {
          map.set(name, {
            name,
            credits: 0,
            projects: 0,
            typologies: {},
            states: {},
          });
        }
        const entry = map.get(name);
        entry.credits += project.credits || 0;
        entry.projects += getProjectWeight(project);
        entry.typologies[project.projectTypology] =
          (entry.typologies[project.projectTypology] || 0) +
          (project.credits || 0);
        entry.states[project.projectState] =
          (entry.states[project.projectState] || 0) + (project.credits || 0);
      });
    });

    const entries = Array.from(map.values()).map((entry) => {
      const weeklyHours = weeklyHoursByName[entry.name] || DEFAULT_WEEKLY_HOURS;
      const loadRatio =
        weeklyHours && entry.credits ? entry.credits / weeklyHours : 0;
      const loadScore = loadRatio;
      return {
        ...entry,
        weeklyHours,
        loadRatio,
        loadScore,
      };
    });

    const maxLoad = entries.reduce(
      (acc, entry) => Math.max(acc, entry.loadRatio),
      0
    );

    return entries
      .map((entry) => ({
        ...entry,
        loadPct: maxLoad ? Math.round((entry.loadRatio / maxLoad) * 100) : 0,
      }))
      .sort((a, b) => b.loadScore - a.loadScore);
  }, [filteredProjects, weeklyHoursByName]);

  const typologyHighlights = useMemo(() => {
    const map = new Map();
    filteredProjects.forEach((project) => {
      const assignees = project.userAssignees || [];
      if (!assignees.length) return;
      const key = project.projectTypology || "Sem tipologia";
      const totalCredits = (project.credits || 0) * assignees.length;
      map.set(key, (map.get(key) || 0) + totalCredits);
    });
    return Array.from(map.entries())
      .map(([label, credits]) => ({ label, credits }))
      .sort((a, b) => b.credits - a.credits);
  }, [filteredProjects]);

  const stateHighlights = useMemo(() => {
    const map = new Map();
    filteredProjects.forEach((project) => {
      const assignees = project.userAssignees || [];
      if (!assignees.length) return;
      const key = project.projectState || "Sem estado";
      const totalCredits = (project.credits || 0) * assignees.length;
      map.set(key, (map.get(key) || 0) + totalCredits);
    });
    return Array.from(map.entries())
      .map(([label, credits]) => ({ label, credits }))
      .sort((a, b) => b.credits - a.credits);
  }, [filteredProjects]);

  const riskHighlights = useMemo(() => {
    const map = new Map();
    filteredProjects.forEach((project) => {
      const assignees = project.userAssignees || [];
      if (!assignees.length) return;
      const key = project.riskLevel || "Sem risco";
      const totalCredits = (project.credits || 0) * assignees.length;
      map.set(key, (map.get(key) || 0) + totalCredits);
    });
    return Array.from(map.entries())
      .map(([label, credits]) => ({ label, credits }))
      .sort((a, b) => b.credits - a.credits);
  }, [filteredProjects]);

  const kpis = useMemo(() => {
    const weightedProjects = filteredProjects.reduce(
      (acc, project) => acc + getProjectWeight(project),
      0
    );
    const avgLoadRatio = employeeStats.length
      ? employeeStats.reduce((acc, entry) => acc + entry.loadRatio, 0) /
        employeeStats.length
      : 0;
    const avgLoadScore = employeeStats.length
      ? employeeStats.reduce((acc, entry) => acc + entry.loadScore, 0) /
        employeeStats.length
      : 0;
    const maxLoadScore = employeeStats.length
      ? Math.max(...employeeStats.map((entry) => entry.loadScore))
      : 0;
    const loads = employeeStats.map((entry) => entry.loadRatio);
    const maxLoad = loads.length ? Math.max(...loads) : 0;
    const minLoad = loads.length ? Math.min(...loads) : 0;
    const spread = maxLoad - minLoad;
    const busiest = employeeStats[0];
    const lowest =
      employeeStats.length > 0 ? employeeStats[employeeStats.length - 1] : null;

    return {
      employees: employeeStats.length,
      projects: weightedProjects,
      avgProjects: employeeStats.length
        ? (weightedProjects / employeeStats.length).toFixed(1)
        : "0",
      avgLoadRatio,
      avgLoadScore,
      maxLoadScore,
      spread,
      busiest,
      lowest,
    };
  }, [employeeStats, filteredProjects]);

  const loadInsights = useMemo(() => {
    const loads = employeeStats.map((entry) => entry.loadScore);
    const avgLoadRatio = kpis.avgLoadRatio;
    const threshold80 = getPercentile(loads, 80);
    const spreadRatio = avgLoadRatio ? kpis.spread / avgLoadRatio : 0;
    const overloaded = employeeStats.filter(
      (entry) => entry.loadScore >= threshold80 && threshold80 > 0
    );

    return {
      threshold80,
      spreadRatio,
      overloaded,
    };
  }, [employeeStats, kpis.avgLoadRatio, kpis.spread]);

  const buildColorMap = (items, palette) => {
    const map = {};
    items.forEach((item, index) => {
      if (palette[index]) {
        map[item] = palette[index];
        return;
      }
      const hue = (index * 137.508) % 360;
      map[item] = `hsl(${hue}, 70%, 50%)`;
    });
    return map;
  };

  const typologyColors = useMemo(() => {
    const palette = [
      "#2563eb",
      "#22c55e",
      "#f97316",
      "#14b8a6",
      "#a855f7",
      "#ef4444",
      "#0ea5e9",
      "#eab308",
      "#7c3aed",
      "#10b981",
    ];
    return buildColorMap(typologyOptions, palette);
  }, [typologyOptions]);

  const stateColors = useMemo(() => {
    const palette = [
      "#0ea5e9",
      "#f59e0b",
      "#ef4444",
      "#22c55e",
      "#8b5cf6",
      "#14b8a6",
      "#f97316",
      "#1d4ed8",
      "#eab308",
      "#7c3aed",
    ];
    return buildColorMap(stateOptions, palette);
  }, [stateOptions]);

  const riskColors = useMemo(() => {
    const palette = ["#22c55e", "#f59e0b", "#ef4444", "#94a3b8"];
    return buildColorMap(riskOptions, palette);
  }, [riskOptions]);

  const consultantColors = useMemo(() => {
    const palette = [
      "#1d4ed8",
      "#14b8a6",
      "#f97316",
      "#7c3aed",
      "#0ea5e9",
      "#22c55e",
      "#ef4444",
      "#a855f7",
      "#eab308",
      "#10b981",
      "#fb7185",
      "#6366f1",
    ];
    return buildColorMap(
      managementUsers.map((user) => user.name),
      palette
    );
  }, [managementUsers]);

  const typologyPieOptions = useMemo(
    () => ({
      chart: { type: "pie", height: 280, backgroundColor: "transparent" },
      title: { text: null },
      tooltip: {
        pointFormat: "<b>{point.percentage:.1f}%</b> ({point.y:.2f})",
      },
      exporting: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          innerSize: "55%",
          dataLabels: {
            enabled: true,
            format: "{point.name}: {point.y:.2f}",
            style: { fontSize: "10px" },
          },
          showInLegend: true,
        },
      },
      legend: { align: "center", verticalAlign: "bottom" },
      series: [
        {
          name: "Créditos",
          data: typologyHighlights.map((item) => ({
            name: item.label,
            y: item.credits,
            color: typologyColors[item.label] || "#94a3b8",
          })),
        },
      ],
      credits: { enabled: false },
    }),
    [typologyHighlights, typologyColors]
  );

  const statePieOptions = useMemo(
    () => ({
      chart: { type: "pie", height: 280, backgroundColor: "transparent" },
      title: { text: null },
      tooltip: {
        pointFormat: "<b>{point.percentage:.1f}%</b> ({point.y:.2f})",
      },
      exporting: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          innerSize: "55%",
          dataLabels: {
            enabled: true,
            format: "{point.name}: {point.y:.2f}",
            style: { fontSize: "10px" },
          },
          showInLegend: true,
        },
      },
      legend: { align: "center", verticalAlign: "bottom" },
      series: [
        {
          name: "Créditos",
          data: stateHighlights.map((item) => ({
            name: item.label,
            y: item.credits,
            color: stateColors[item.label] || "#94a3b8",
          })),
        },
      ],
      credits: { enabled: false },
    }),
    [stateHighlights, stateColors]
  );

  const consultantPieOptions = useMemo(
    () => ({
      chart: { type: "pie", height: 280, backgroundColor: "transparent" },
      title: { text: null },
      tooltip: {
        pointFormat: "<b>{point.percentage:.1f}%</b> ({point.y:.2f})",
      },
      exporting: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          innerSize: "55%",
          dataLabels: {
            enabled: true,
            format: "{point.name}: {point.y:.2f}",
            style: { fontSize: "10px" },
          },
          showInLegend: true,
        },
      },
      legend: { align: "center", verticalAlign: "bottom" },
      series: [
        {
          name: "Créditos",
          data: employeeStats.map((entry) => ({
            name: entry.name,
            y: entry.credits,
            color: consultantColors[entry.name] || "#94a3b8",
          })),
        },
      ],
      credits: { enabled: false },
    }),
    [employeeStats, consultantColors]
  );

  const riskPieOptions = useMemo(
    () => ({
      chart: { type: "pie", height: 280, backgroundColor: "transparent" },
      title: { text: null },
      tooltip: {
        pointFormat: "<b>{point.percentage:.1f}%</b> ({point.y:.2f})",
      },
      exporting: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          innerSize: "55%",
          dataLabels: {
            enabled: true,
            format: "{point.name}: {point.y:.2f}",
            style: { fontSize: "10px" },
          },
          showInLegend: true,
        },
      },
      legend: { align: "center", verticalAlign: "bottom" },
      series: [
        {
          name: "Créditos",
          data: riskHighlights.map((item) => ({
            name: item.label,
            y: item.credits,
            color: riskColors[item.label] || "#94a3b8",
          })),
        },
      ],
      credits: { enabled: false },
    }),
    [riskHighlights, riskColors]
  );

  const loadDistributionOptions = useMemo(
    () => ({
      chart: { type: "bar", height: 360, backgroundColor: "transparent" },
      title: { text: null },
      exporting: {
        enabled: false,
      },
      xAxis: {
        categories: employeeStats.map((entry) => entry.name),
        title: { text: null },
        lineColor: "#e2e8f0",
        tickColor: "#e2e8f0",
        labels: {
          useHTML: true,
          style: { color: "#475569", fontSize: "11px" },
          formatter() {
            const name = this.value;
            const avatar = avatarByName[name];
            if (avatar) {
              return `<span class="resources-axis-avatar"><img src="${avatar}" alt="${name}"/>${name}</span>`;
            }
            const color = consultantColors[name] || "#e2e8f0";
            return `<span class="resources-axis-avatar resources-axis-avatar--fallback" style="--axis-color:${color};">${name}</span>`;
          },
        },
      },
      yAxis: {
        min: 0,
        title: { text: "Carga (CR/H)" },
        gridLineColor: "#e2e8f0",
        labels: { style: { color: "#475569", fontSize: "11px" } },
        plotLines: [
          {
            value: kpis.avgLoadRatio,
            color: "#94a3b8",
            width: 2,
            zIndex: 5,
            label: {
              text: "Média da equipa",
              align: "right",
              style: { color: "#64748b" },
            },
          },
        ],
      },
      tooltip: {
        pointFormatter() {
          const entry = employeeStats[this.index];
          return `<b>${entry.name}</b><br/>${entry.credits.toFixed(
            1
          )} créditos / ${
            entry.weeklyHours
          }h<br/>Carga: ${entry.loadRatio.toFixed(3)} cr/h`;
        },
      },
      plotOptions: {
        series: {
          borderRadius: 4,
          pointPadding: 0.2,
          groupPadding: 0.1,
          dataLabels: {
            enabled: true,
            format: "{point.y:.3f}",
            style: { fontSize: "10px", color: "#0f172a" },
          },
        },
      },
      legend: { enabled: false },
      series: [
        {
          name: "Carga",
          data: employeeStats.map((entry) => ({
            y: entry.loadRatio,
            color: consultantColors[entry.name] || "#94a3b8",
          })),
        },
      ],
      credits: { enabled: false },
    }),
    [employeeStats, kpis.avgLoadRatio, consultantColors, avatarByName]
  );

  const selectedProjects = useMemo(() => {
    if (!selectedConsultant) return [];
    return filteredProjects
      .filter((project) =>
        project.userAssignees?.includes(selectedConsultant.name)
      )
      .map((project) => ({
        ...project,
        credit: project.credits || 0,
        shared: (project.userAssignees || []).length > 1,
      }));
  }, [filteredProjects, selectedConsultant]);

  const saveWeeklyHours = () => {
    apiClient
      .post("/projects/resources/", {
        hours: Object.fromEntries(
          Object.entries(hoursDraft).filter(
            ([, value]) => value !== "" && Number.isFinite(Number(value))
          )
        ),
      })
      .then((response) => {
        const users = response.data.management_users || [];
        const nameHoursMap = {};
        users.forEach((user) => {
          nameHoursMap[user.name] = Number(user.weekly_hours);
        });
        setManagementUsers(users);
        setWeeklyHoursByName(nameHoursMap);
        setHoursOpen(false);
      })
      .catch(() => {
        setError(true);
      });
  };

  if (error) return <ErrorPage message="Erro ao carregar recursos." />;
  if (!projects) return <LoadingScreen message="A carregar recursos ..." />;

  return (
    <CContainer>
      <CCardBody>
        <CRow className="g-3 mb-4 align-items-stretch">
          <CCol sm={6} lg={3}>
              <CWidgetStatsB
              className="resources-kpi h-100 kpi-total"
              value={formatProjectCount(kpis.projects)}
              title="Projetos ativos"
              text={`Média por consultor: ${kpis.avgProjects}`}
            />
          </CCol>
          <CCol sm={6} lg={3}>
            <CWidgetStatsB
              className="resources-kpi h-100 kpi-completion"
              value={kpis.avgLoadRatio.toFixed(3)}
              title="Carga média"
              text={`Desvio máximo: ${kpis.spread.toFixed(3)}`}
              progress={{
                value: kpis.maxLoadScore
                  ? Math.min((kpis.avgLoadScore / kpis.maxLoadScore) * 100, 100)
                  : 0,
              }}
            />
          </CCol>
          <CCol sm={6} lg={3}>
            <CWidgetStatsB
              className="resources-kpi h-100 kpi-overdue"
              value={kpis.busiest ? kpis.busiest.name : "-"}
              title="Consultor mais carregado"
              text={
                kpis.busiest
                  ? `Carga atual: ${kpis.busiest.loadRatio.toFixed(3)} cr/h`
                  : "-"
              }
            />
          </CCol>
          <CCol sm={6} lg={3}>
            <CWidgetStatsB
              className="resources-kpi h-100 kpi-pending"
              value={kpis.lowest ? kpis.lowest.name : "-"}
              title="Consultor com mais margem"
              text={
                kpis.lowest
                  ? `Carga atual: ${kpis.lowest.loadRatio.toFixed(3)} cr/h`
                  : "-"
              }
            />
          </CCol>
        </CRow>

        <CRow className="g-3 mb-4 resources-insights">
          {loadInsights.overloaded.length ? (
            <CCol lg={4}>
              <div className="resources-insight resources-insight--danger">
                <div className="resources-insight-icon">
                  <CIcon icon={cilWarning} />
                </div>
                <div>
                  <div className="resources-insight-title">
                    Top 20% da carga
                  </div>
                  <div className="resources-insight-text">
                    20% mais carregados:{" "}
                    {loadInsights.overloaded
                      .map((entry) => entry.name)
                      .join(", ")}
                  </div>
                </div>
              </div>
            </CCol>
          ) : null}
          {loadInsights.spreadRatio > 0.3 ? (
            <CCol lg={4}>
              <div className="resources-insight resources-insight--warning">
                <div className="resources-insight-icon">
                  <CIcon icon={cilChartLine} />
                </div>
                <div>
                  <div className="resources-insight-title">
                    Diferença de carga alta
                  </div>
                  <div className="resources-insight-text">
                    Diferença &gt; 30% entre consultores.
                  </div>
                </div>
              </div>
            </CCol>
          ) : null}
          {kpis.lowest && kpis.busiest ? (
            <CCol lg={4}>
              <div className="resources-insight resources-insight--info">
                <div className="resources-insight-icon">
                  <CIcon icon={cilLightbulb} />
                </div>
                <div>
                  <div className="resources-insight-title">Sugestão</div>
                  <div className="resources-insight-text">
                    {kpis.lowest.name} com mais margem.
                  </div>
                </div>
              </div>
            </CCol>
          ) : null}
        </CRow>

        <div className="management-filters mb-4">
          <div className="management-filters-header">
            <div className="management-section-title">Filtros</div>
            <CButton
              color="light"
              size="sm"
              onClick={() =>
                setFilters({
                  search: "",
                  typology: [],
                  states: [],
                  risk: [],
                  employee: [],
                  publicContracting: [],
                })
              }
            >
              Limpar filtros
            </CButton>
          </div>
          <CRow className="g-3 management-filters-grid resources-filters-grid">
            <CCol xs={12} md={6} lg={4}>
              <div className="resources-filter-field">
                <CFormLabel>Pesquisar</CFormLabel>
                <CFormInput
                  placeholder="Projeto, tipologia, consultor"
                  value={filters.search}
                  onChange={(event) =>
                    setFilters((prev) => ({
                      ...prev,
                      search: event.target.value,
                    }))
                  }
                />
              </div>
            </CCol>
            <CCol xs={12} md={6} lg={3}>
              <SearchSelect
                label="Estado do projeto"
                isMulti
                options={stateOptions.map((option) => ({
                  label: option,
                  value: option,
                }))}
                value={filters.states}
                onChange={(values) =>
                  setFilters((prev) => ({ ...prev, states: values }))
                }
                placeholder="Selecione estados"
              />
            </CCol>
            <CCol xs={12} md={6} lg={3}>
              <SearchSelect
                label="Tipologia de projeto"
                isMulti
                options={typologyOptions.map((option) => ({
                  label: option,
                  value: option,
                }))}
                value={filters.typology}
                onChange={(values) =>
                  setFilters((prev) => ({ ...prev, typology: values }))
                }
                placeholder="Selecione tipologias"
              />
            </CCol>
            <CCol xs={12} md={6} lg={2}>
              <SearchSelect
                label="Risco"
                isMulti
                options={riskOptions.map((option) => ({
                  label: option,
                  value: option,
                }))}
                value={filters.risk}
                onChange={(values) =>
                  setFilters((prev) => ({ ...prev, risk: values }))
                }
                placeholder="Selecione riscos"
              />
            </CCol>
            <CCol xs={12} md={6} lg={3}>
              <SearchSelect
                label="Consultor"
                isMulti
                options={employeeOptions.map((option) => ({
                  label: option,
                  value: option,
                }))}
                value={filters.employee}
                onChange={(values) =>
                  setFilters((prev) => ({ ...prev, employee: values }))
                }
                placeholder="Selecione consultores"
              />
            </CCol>
            <CCol xs={12} md={6} lg={3}>
              <SearchSelect
                label="Contratação Pública"
                isMulti
                options={publicContractingOptions.map((option) => ({
                  label: option || "Não",
                  value: option,
                }))}
                value={filters.publicContracting}
                onChange={(values) =>
                  setFilters((prev) => ({ ...prev, publicContracting: values }))
                }
                placeholder="Selecione"
              />
            </CCol>
          </CRow>
          <div className="management-active-filters">
            {[
              filters.search && `Pesquisa: ${filters.search}`,
              filters.typology.length &&
                `Tipologia: ${filters.typology.join(", ")}`,
              filters.states.length && `Estados: ${filters.states.join(", ")}`,
              filters.risk.length && `Risco: ${filters.risk.join(", ")}`,
              filters.employee.length &&
                `Consultor: ${filters.employee.join(", ")}`,
              filters.publicContracting.length &&
                `Contratação Pública: ${filters.publicContracting
                  .map((pb) => pb || "Não")
                  .join(", ")}`,
            ]
              .filter(Boolean)
              .map((filter) => (
                <CBadge key={filter} color="info" className="projects-badge">
                  {filter}
                </CBadge>
              ))}
            {!filters.search &&
            !filters.typology.length &&
            !filters.states.length &&
            !filters.risk.length &&
            !filters.employee.length ? (
              <span className="text-medium-emphasis small">
                Sem filtros aplicados.
              </span>
            ) : null}
          </div>
        </div>

        <CRow className="g-3 mb-4">
          <CCol lg={8}>
            <CCard className="resources-table-card">
              <CCardBody>
                <div className="resources-section-header">
                  <div className="resources-section-title">
                    Carga por consultor
                  </div>
                  <CButton
                    color="light"
                    size="sm"
                    className="resources-gear"
                    onClick={() => setHoursOpen(true)}
                  >
                    <CIcon icon={cilSettings} />
                  </CButton>
                </div>
                <CTable className="resources-table" hover responsive>
                  <thead>
                    <tr>
                      <th>Colaborador</th>
                      <th>Horas semanais</th>
                      <th>Nº projetos</th>
                      <th>Créditos</th>
                      <th>Carga (CR/H)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employeeStats.map((entry) => (
                      <tr
                        key={entry.name}
                        className="resources-row"
                        onClick={() => {
                          setSelectedConsultant(entry);
                          setDetailsOpen(true);
                        }}
                      >
                        <td>
                          <div className="resources-consultant">
                            {avatarByName[entry.name] ? (
                              <img
                                className="resources-consultant-avatar"
                                src={avatarByName[entry.name]}
                                alt={entry.name}
                              />
                            ) : (
                              <div
                                className="resources-consultant-avatar resources-consultant-avatar--fallback"
                                style={{
                                  backgroundColor:
                                    consultantColors[entry.name] || "#e2e8f0",
                                  color: "#fff",
                                }}
                              >
                                {entry.name?.charAt(0) || "?"}
                              </div>
                            )}
                            <div>
                              <div className="fw-semibold">{entry.name}</div>
                              <div className="text-medium-emphasis small">
                                {entry.weeklyHours}h semanais
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>{entry.weeklyHours}h</td>
                        <td>{formatProjectCount(entry.projects)}</td>
                        <td>
                          <CBadge color="info" className="resources-chip">
                            {entry.credits.toFixed(2)}
                          </CBadge>
                        </td>
                        <td className="resources-load-cell">
                          {entry.loadRatio.toFixed(3)}
                        </td>
                      </tr>
                    ))}
                    {!employeeStats.length ? (
                      <tr>
                        <td colSpan={5} className="text-center text-muted">
                          Sem colaboradores para os filtros atuais.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </CTable>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol lg={4}>
            <CCard className="resources-table-card">
              <CCardBody>
                <div className="resources-section-title">Análise</div>
                <CTabs activeItemKey={0}>
                  <CTabList variant="pills" className="resources-tabs">
                    <CTab itemKey={0}>Tipologia</CTab>
                    <CTab itemKey={1}>Estado</CTab>
                    <CTab itemKey={2}>Consultor</CTab>
                    <CTab itemKey={3}>Risco</CTab>
                  </CTabList>
                  <CTabContent>
                    <CTabPanel itemKey={0}>
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={typologyPieOptions}
                      />
                    </CTabPanel>
                    <CTabPanel itemKey={1}>
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={statePieOptions}
                      />
                    </CTabPanel>
                    <CTabPanel itemKey={2}>
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={consultantPieOptions}
                      />
                    </CTabPanel>
                    <CTabPanel itemKey={3}>
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={riskPieOptions}
                      />
                    </CTabPanel>
                  </CTabContent>
                </CTabs>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <CCard className="resources-table-card mb-4">
          <CCardBody>
            <div className="resources-section-title">Distribuição da carga</div>
            <HighchartsReact
              highcharts={Highcharts}
              options={loadDistributionOptions}
            />
          </CCardBody>
        </CCard>

        <CCard className="resources-table-card">
          <CCardBody>
            <div className="resources-section-title">Projetos</div>
            <CTable className="resources-table" striped hover responsive>
              <thead>
                <tr>
                  <th>Projeto</th>
                  <th>Tipologia</th>
                  <th>Estado</th>
                  <th>Co. Pública</th>
                  <th>Risco</th>
                  <th>Créditos</th>
                  <th>Responsáveis</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((project) => (
                  <tr
                    key={project.id}
                    onClick={() =>
                      window.open(
                        `https://streamconsulting.teamwork.com/app/tasks/${project.id}`,
                        "_blank"
                      )
                    }
                  >
                    <td>{project.project}</td>
                    <td>{project.projectTypology}</td>
                    <td>
                      <CBadge
                        className="resources-chip"
                        style={{
                          backgroundColor:
                            stateColors[project.projectState] || "#94a3b8",
                          color: "#fff",
                        }}
                      >
                        {project.projectState}
                      </CBadge>
                    </td>
                    <td>{project.publicContracting || "Não"}</td>
                    <td>
                      {project.riskLevel ? (
                        <CBadge
                          className="resources-chip"
                          style={{
                            backgroundColor:
                              riskColors[project.riskLevel] || "#94a3b8",
                            color: "#fff",
                          }}
                        >
                          {project.riskLevel}
                        </CBadge>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{Number(project.credits || 0).toFixed(2)}</td>
                    <td>
                      {(project.userAssignees || []).map((assignee) => (
                        <CBadge
                          key={`${project.id}-${assignee}`}
                          className="resources-chip"
                          style={{
                            backgroundColor:
                              consultantColors[assignee] || "#576169aa",
                            color: "#fff",
                          }}
                        >
                          {assignee}
                        </CBadge>
                      ))}
                    </td>
                  </tr>
                ))}
                {!filteredProjects.length ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted">
                      Sem projetos para os filtros atuais.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </CTable>
          </CCardBody>
        </CCard>

        <COffcanvas
          placement="end"
          visible={detailsOpen}
          onHide={() => setDetailsOpen(false)}
          className="resources-offcanvas"
        >
          <COffcanvasHeader>
            <COffcanvasTitle>
              Carteira de projetos – {selectedConsultant?.name || ""}
            </COffcanvasTitle>
          </COffcanvasHeader>
          <COffcanvasBody>
            {selectedConsultant ? (
              <>
                <div className="resources-detail-summary">
                  <div>
                    <span className="text-medium-emphasis">Horas semanais</span>
                    <div className="fw-semibold">
                      {selectedConsultant.weeklyHours}h
                    </div>
                  </div>
                  <div>
                    <span className="text-medium-emphasis">
                      Créditos atribuídos
                    </span>
                    <div className="fw-semibold">
                      {selectedConsultant.credits.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <span className="text-medium-emphasis">Carga</span>
                    <div className="fw-semibold">
                      {selectedConsultant.loadRatio.toFixed(3)} cr/h
                    </div>
                  </div>
                  <div>
                    <span className="text-medium-emphasis">
                      Posição na equipa
                    </span>
                    <div className="fw-semibold">
                      {employeeStats.findIndex(
                        (entry) => entry.name === selectedConsultant.name
                      ) + 1}
                      º
                    </div>
                  </div>
                </div>
                <CTable className="resources-table" striped hover responsive>
                  <thead>
                    <tr>
                      <th>Projeto</th>
                      <th>Tipologia</th>
                      <th>Estado</th>
                      <th>Copromoção</th>
                      <th>Risco</th>
                      <th>Créditos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedProjects.map((project) => (
                      <tr key={project.id}>
                        <td>{project.project}</td>
                        <td>{project.projectTypology}</td>
                        <td>
                          <CBadge
                            className="resources-chip"
                            style={{
                              backgroundColor:
                                stateColors[project.projectState] || "#94a3b8",
                              color: "#fff",
                            }}
                          >
                            {project.projectState}
                          </CBadge>
                        </td>
                        <td>{project.publicContracting || "Não"}</td>
                        <td>
                          {project.riskLevel ? (
                            <CBadge
                              className="resources-chip"
                              style={{
                                backgroundColor:
                                  riskColors[project.riskLevel] || "#94a3b8",
                                color: "#fff",
                              }}
                            >
                              {project.riskLevel}
                            </CBadge>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>{project.credit.toFixed(2)}</td>
                      </tr>
                    ))}
                    {!selectedProjects.length ? (
                      <tr>
                        <td colSpan={6} className="text-muted text-center">
                          Sem projetos atribuídos.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </CTable>
              </>
            ) : (
              <div className="text-muted">Selecione um consultor.</div>
            )}
          </COffcanvasBody>
        </COffcanvas>

        <COffcanvas
          placement="end"
          visible={hoursOpen}
          onHide={() => setHoursOpen(false)}
          className="resources-offcanvas"
        >
          <COffcanvasHeader>
            <COffcanvasTitle>Horas semanais</COffcanvasTitle>
          </COffcanvasHeader>
          <COffcanvasBody>
            <div className="resources-hours-list">
              {managementUsers.map((user) => (
                <div key={user.teamwork_id} className="resources-hours-row">
                  <div className="resources-hours-info">
                    {user.avatar_url ? (
                      <img
                        className="resources-hours-avatar"
                        src={user.avatar_url}
                        alt={user.name}
                      />
                    ) : (
                      <div
                        className="resources-hours-avatar resources-hours-avatar--fallback"
                        style={{
                          backgroundColor:
                            consultantColors[user.name] || "#e2e8f0",
                          color: "#fff",
                        }}
                      >
                        {user.name?.charAt(0) || "?"}
                      </div>
                    )}
                    <div>
                      <div className="fw-semibold">{user.name}</div>
                      <div className="text-medium-emphasis small">
                        Teamwork #{user.teamwork_id}
                      </div>
                    </div>
                  </div>
                  <CFormInput
                    type="number"
                    min="0"
                    step="0.5"
                    value={hoursDraft[user.teamwork_id] ?? DEFAULT_WEEKLY_HOURS}
                    onChange={(event) =>
                      setHoursDraft((prev) => ({
                        ...prev,
                        [user.teamwork_id]:
                          event.target.value === ""
                            ? ""
                            : Number(event.target.value),
                      }))
                    }
                  />
                </div>
              ))}
              {!managementUsers.length ? (
                <div className="text-muted small">Sem utilizadores.</div>
              ) : null}
            </div>
            <div className="resources-hours-actions">
              <CButton color="light" onClick={() => setHoursOpen(false)}>
                Cancelar
              </CButton>
              <CButton color="primary" onClick={saveWeeklyHours}>
                Guardar
              </CButton>
            </div>
          </COffcanvasBody>
        </COffcanvas>
      </CCardBody>
    </CContainer>
  );
};

export default Resources;
